import { useState, useEffect, useMemo } from 'react';
import { useDebounce } from '@/hooks/use-debounce';
import { extractCommunitiesFromText, ExtractedCommunity } from '@/utils/communityExtraction';
import { searchLocations, detectLocationsInText } from '@/data/locations';
import { supabase } from '@/integrations/supabase/client';

export interface RealtimeCommunity extends ExtractedCommunity {
  id: string;
  displayName: string;
  color?: string;
  gradient?: string;
  icon?: string;
}

export function useRealtimeCommunityExtraction(text: string) {
  const [predictedCommunities, setPredictedCommunities] = useState<RealtimeCommunity[]>([]);
  const [isExtracting, setIsExtracting] = useState(false);
  const [existingCommunities, setExistingCommunities] = useState<Map<string, any>>(new Map());
  
  // Debounce the text input to avoid excessive API calls
  const debouncedText = useDebounce(text, 500);

  // Fetch existing communities on mount for better matching
  useEffect(() => {
    async function fetchExistingCommunities() {
      try {
        const { data } = await supabase
          .from('communities')
          .select('id, name, display_name, is_fomo_official, color, gradient, icon');
        
        if (data) {
          const communityMap = new Map();
          data.forEach(community => {
            communityMap.set(community.name, community);
          });
          setExistingCommunities(communityMap);
        }
      } catch (error) {
        console.error('Error fetching communities for preview:', error);
      }
    }
    
    fetchExistingCommunities();
  }, []);

  useEffect(() => {
    async function extractCommunities() {
      const raw = debouncedText || '';
      const activeTagMatch = raw.match(/(^|\s)#([a-z0-9_-]*)$/i);

      // Helper to synthesize gradient from color (or use a default)
      const synthGradient = (hex?: string) => {
        if (!hex || !/^#?[0-9a-fA-F]{3,6}$/.test(hex)) return 'linear-gradient(90deg, #A78BFA, #C4B5FD)';
        const norm = hex.startsWith('#') ? hex : `#${hex}`;
        const x = norm.length === 3 ? norm.split('').map(c => c + c).join('') : norm;
        const r = parseInt(x.slice(0,2), 16), g = parseInt(x.slice(2,4), 16), b = parseInt(x.slice(4,6), 16);
        const L = (v: number) => Math.min(255, Math.floor(v + (255 - v) * 0.35));
        const toHex = (v: number) => v.toString(16).padStart(2, '0');
        return `linear-gradient(90deg, ${norm}, #${toHex(L(r))}${toHex(L(g))}${toHex(L(b))})`;
      };

      // If user is typing a hashtag, surface predictive results immediately
      if (activeTagMatch) {
        const partial = (activeTagMatch[2] || '').toLowerCase();
        const all = Array.from(existingCommunities.values()) as Array<{
          id: string; name: string; display_name: string; is_fomo_official?: boolean; color?: string; gradient?: string; icon?: string;
        }>;

        const baseList = all
          .filter(c => !partial || c.name.toLowerCase().startsWith(partial))
          .sort((a, b) => {
            const ao = a.is_fomo_official ? 1 : 0;
            const bo = b.is_fomo_official ? 1 : 0;
            if (bo !== ao) return bo - ao; // official first
            return a.name.localeCompare(b.name);
          })
          .slice(0, 10)
          .map(c => ({
            id: c.id,
            displayName: c.display_name || c.name.charAt(0).toUpperCase() + c.name.slice(1),
            communityName: c.name,
            confidence: 0.99,
            source: 'manual' as const,
            isOfficial: !!c.is_fomo_official,
            color: c.color,
            gradient: c.gradient || synthGradient(c.color),
            icon: c.icon || (c.is_fomo_official ? '🎯' : '✨'),
          }));

        // Also include location matches from gazetteer on the same partial
        const locs = searchLocations(partial, 10).map(l => ({
          id: `loc-${l.slug}`,
          displayName: l.name,
          communityName: l.slug,
          confidence: 0.95,
          source: 'manual' as const,
          isOfficial: false,
          color: '#60A5FA',
          gradient: 'linear-gradient(90deg, #60A5FA, #A78BFA)',
          icon: '📍',
        }));

        // merge and de-dupe by communityName
        const merged = [...baseList as any[], ...locs].reduce((acc: any[], item: any) => {
          if (!acc.find(x => x.communityName === item.communityName)) acc.push(item);
          return acc;
        }, []);

        setPredictedCommunities(merged as any);
        return;
      }

      if (!raw.trim() || raw.length < 3) {
        setPredictedCommunities([]);
        return;
      }

      setIsExtracting(true);
      
      try {
        const extracted = await extractCommunitiesFromText(raw, existingCommunities);
        
        // Transform to RealtimeCommunity format with additional display info
        const realtimeCommunities: RealtimeCommunity[] = extracted
          .filter(community => community.confidence > 0.4) // Only show higher confidence predictions
          .map(community => {
            const existingData = existingCommunities.get(community.communityName);
            
            return {
              ...community,
              id: existingData?.id || `preview-${community.communityName}`,
              displayName: existingData?.display_name || 
                         community.communityName.charAt(0).toUpperCase() + community.communityName.slice(1),
              color: existingData?.color,
              gradient: existingData?.gradient || synthGradient(existingData?.color),
              icon: existingData?.icon || (community.isOfficial ? '🎯' : '✨')
            };
          })
          .slice(0, 10); // Show more communities so users can see all predictions

        // Also detect locations directly from plain text and merge
        const locsInText = detectLocationsInText(raw).map(l => ({
          id: `loc-${l.slug}`,
          displayName: l.name,
          communityName: l.slug,
          confidence: 0.9,
          source: 'manual' as const,
          isOfficial: false,
          color: '#60A5FA',
          gradient: 'linear-gradient(90deg, #60A5FA, #A78BFA)',
          icon: '📍',
        }));

        const merged = [...realtimeCommunities, ...locsInText].reduce((acc: any[], item: any) => {
          if (!acc.find(x => x.communityName === item.communityName)) acc.push(item);
          return acc;
        }, []);

        setPredictedCommunities(merged as any);
      } catch (error) {
        console.error('Error extracting communities in real-time:', error);
        setPredictedCommunities([]);
      } finally {
        setIsExtracting(false);
      }
    }

    extractCommunities();
  }, [debouncedText, existingCommunities]);

  // Group communities by confidence level for different styling
  const groupedCommunities = useMemo(() => {
    const high = predictedCommunities.filter(c => c.confidence >= 0.8);
    const medium = predictedCommunities.filter(c => c.confidence >= 0.6 && c.confidence < 0.8);
    const low = predictedCommunities.filter(c => c.confidence < 0.6);
    
    return { high, medium, low };
  }, [predictedCommunities]);

  return {
    predictedCommunities,
    groupedCommunities,
    isExtracting,
    hasPredictions: predictedCommunities.length > 0
  };
}